import { useState, useMemo } from 'react';

interface RuleTagsMap {
  [ruleId: string]: string[]; // ruleId -> array of coreEnumOptionIds
}

interface UseRuleTagFilteringReturn {
  selectedTagIds: Set<string>;
  setSelectedTagIds: (tags: Set<string>) => void;
  ruleTagsMap: RuleTagsMap;
  availableTagIds: Set<string>;
  matchesFilters: (ruleId: string) => boolean; // Returns true if rule matches selected filters
  filteredRuleIds: Set<string>; // Rules that match the selected filters
}

interface RuleWithTags {
  id: string;
  tags?: Array<{ id: string; name: string }>;
}

/**
 * Hook to manage tag filtering for rebate rules
 * Uses OR logic: a rule must have ANY selected tag to match
 *
 * Tags are provided directly from the forecast data (no lazy loading needed)
 */
export function useRuleTagFiltering(
  rules: RuleWithTags[] | undefined,
): UseRuleTagFilteringReturn {
  const [selectedTagIds, setSelectedTagIds] = useState<Set<string>>(new Set());

  // Build ruleTagsMap directly from rules array
  const ruleTagsMap = useMemo(() => {
    const tagsMap: RuleTagsMap = {};
    if (!rules) return tagsMap;

    rules.forEach((rule) => {
      tagsMap[rule.id] = rule.tags?.map((tag) => tag.id) || [];
    });

    return tagsMap;
  }, [rules]);

  // Collect all unique tag IDs available across all rules
  const availableTagIds = useMemo(() => {
    const tags = new Set<string>();
    Object.values(ruleTagsMap).forEach((ruleTags) => {
      ruleTags.forEach((tagId) => tags.add(tagId));
    });
    return tags;
  }, [ruleTagsMap]);

  // Helper: Check if a rule matches the selected filters (OR logic)
  const matchesFilters = useMemo(() => {
    return (ruleId: string): boolean => {
      if (selectedTagIds.size === 0) {
        return true; // No filter = all rules match
      }

      const ruleTags = ruleTagsMap[ruleId] || [];
      // Check if rule has ANY selected tag
      return Array.from(selectedTagIds).some((tagId) =>
        ruleTags.includes(tagId),
      );
    };
  }, [ruleTagsMap, selectedTagIds]);

  // Get all rule IDs that match the current filters
  const filteredRuleIds = useMemo(() => {
    if (!rules) return new Set<string>();

    if (selectedTagIds.size === 0) {
      // No tags selected = all rules match
      return new Set(rules.map((r) => r.id));
    }

    // Filter rules that have ANY selected tag
    const filtered = new Set<string>();
    rules.forEach((rule) => {
      if (matchesFilters(rule.id)) {
        filtered.add(rule.id);
      }
    });

    return filtered;
  }, [rules, matchesFilters, selectedTagIds]);

  return {
    selectedTagIds,
    setSelectedTagIds,
    ruleTagsMap,
    availableTagIds,
    matchesFilters,
    filteredRuleIds,
  };
}
